import React, { useMemo, useState, useCallback } from "react";
import { t, Trans } from "@lingui/macro";
import cx from "classnames";

import { USD_DECIMALS } from "config/factors";
import { formatAmount } from "lib/numbers";
import Tooltip from "components/Tooltip/Tooltip";
import Modal from "components/Modal/Modal";
import { closeAllPositions } from "domain/legacy";
import { useChainId } from "lib/chains";
import { useEthersSigner } from "lib/wallets/useEthersSigner";
import { useExecutionFee } from "domain/legacy";
import useWallet from "lib/wallets/useWallet";
import { useInfoTokens } from "domain/tokens";
import { helperToast } from "lib/helperToast";

import "./AccountSummaryBar.css";

type Position = {
  size: bigint;
  collateral: bigint;
  collateralAfterFee?: bigint;
  pendingDelta?: bigint;
  hasProfit?: boolean;
  pendingDeltaAfterFees?: bigint;
  hasProfitAfterFees?: boolean;
  fundingFee?: bigint;
  indexToken?: { address: string; isNative?: boolean; symbol: string };
  collateralToken?: { address: string };
  isLong?: boolean;
  isSynth?: boolean;
};

type Props = {
  positions: Position[];
  account: string | undefined;
  showPnlAfterFees: boolean;
  setPendingTxns?: (txns: any) => void;
};

function formatUsdShort(val: bigint): string {
  const n = Number(val) / 10 ** USD_DECIMALS;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${n < 0 ? "-" : ""}$${(abs / 1_000).toFixed(1)}K`;
  return `${n < 0 ? "-$" : "$"}${abs.toFixed(2)}`;
}

export function AccountSummaryBar({ positions, account, showPnlAfterFees, setPendingTxns }: Props) {
  const { chainId } = useChainId();
  const { active } = useWallet();
  const signer = useEthersSigner();
  const { infoTokens } = useInfoTokens(signer, chainId, active);
  const { minExecutionFee } = useExecutionFee(active, chainId, infoTokens);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleCloseAll = useCallback(async () => {
    if (!account || !signer || !minExecutionFee) return;
    const closeable = positions.filter(
      (p) => p.size && p.size > 0n && p.indexToken && p.collateralToken
    ) as Required<Pick<Position, "indexToken" | "collateralToken" | "isLong" | "size">>[];
    if (!closeable.length) return;

    setClosing(true);
    try {
      await closeAllPositions(chainId, signer, account, closeable, minExecutionFee, {
        setPendingTxns,
        sentMsg: t`Closing ${closeable.length} position${closeable.length > 1 ? "s" : ""}...`,
        failMsg: t`Failed to close positions`,
      });
      helperToast.success(t`Close requests submitted for ${closeable.length} position${closeable.length > 1 ? "s" : ""}`);
    } catch (e) {
      helperToast.error(t`Error closing positions`);
    } finally {
      setClosing(false);
      setConfirmOpen(false);
    }
  }, [account, signer, minExecutionFee, positions, chainId, setPendingTxns]);

  const summary = useMemo(() => {
    if (!positions || positions.length === 0) {
      return { totalPnl: 0n, totalSize: 0n, totalCollateral: 0n, posCount: 0, pnlPositive: true };
    }

    let totalPnl = 0n;
    let totalSize = 0n;
    let totalCollateral = 0n;

    for (const pos of positions) {
      const delta = showPnlAfterFees ? (pos.pendingDeltaAfterFees ?? pos.pendingDelta ?? 0n) : (pos.pendingDelta ?? 0n);
      const hasProfit = showPnlAfterFees ? (pos.hasProfitAfterFees ?? pos.hasProfit ?? false) : (pos.hasProfit ?? false);
      totalPnl += hasProfit ? delta : -delta;
      totalSize += pos.size ?? 0n;
      totalCollateral += pos.collateralAfterFee ?? pos.collateral ?? 0n;
    }

    return {
      totalPnl,
      totalSize,
      totalCollateral,
      posCount: positions.length,
      pnlPositive: totalPnl >= 0n,
    };
  }, [positions, showPnlAfterFees]);

  if (!account) return null;

  const marginUsagePct =
    summary.totalSize > 0n && summary.totalCollateral > 0n
      ? Math.round((Number(summary.totalSize) / Number(summary.totalCollateral)) * 100) / 100
      : 0;

  const pnlStr = formatUsdShort(summary.totalPnl < 0n ? -summary.totalPnl : summary.totalPnl);
  const pnlDisplay = summary.totalPnl === 0n
    ? "$0.00"
    : summary.pnlPositive
    ? `+${pnlStr}`
    : `-${pnlStr}`;

  return (
    <div className="AccountSummaryBar">
      <div className="ASB-stat">
        <span className="ASB-stat__label"><Trans>Positions</Trans></span>
        <span className="ASB-stat__value">{summary.posCount}</span>
      </div>

      <div className="ASB-divider" />

      <div className="ASB-stat">
        <span className="ASB-stat__label">
          {showPnlAfterFees ? t`Unrealised PnL (after fees)` : t`Unrealised PnL`}
        </span>
        <Tooltip
          handle={
            <span
              className={cx("ASB-stat__value ASB-pnl", {
                positive: summary.pnlPositive && summary.totalPnl > 0n,
                negative: !summary.pnlPositive && summary.totalPnl !== 0n,
                muted: summary.totalPnl === 0n,
              })}
            >
              {pnlDisplay}
            </span>
          }
          position="bottom-start"
          renderContent={() => (
            <div>
              <Trans>Sum of unrealised P&L across all open positions.</Trans>
              {showPnlAfterFees && (
                <div className="muted" style={{ marginTop: "0.4rem" }}>
                  <Trans>Includes open borrowing fees.</Trans>
                </div>
              )}
            </div>
          )}
        />
      </div>

      <div className="ASB-divider" />

      <div className="ASB-stat">
        <span className="ASB-stat__label"><Trans>Total Size</Trans></span>
        <span className="ASB-stat__value">
          {summary.totalSize > 0n ? formatAmount(summary.totalSize, USD_DECIMALS, 2, true, "$") : "$0"}
        </span>
      </div>

      <div className="ASB-stat">
        <span className="ASB-stat__label"><Trans>Total Collateral</Trans></span>
        <span className="ASB-stat__value">
          {summary.totalCollateral > 0n ? formatAmount(summary.totalCollateral, USD_DECIMALS, 2, true, "$") : "$0"}
        </span>
      </div>

      {marginUsagePct > 0 && (
        <>
          <div className="ASB-divider" />
          <div className="ASB-stat">
            <span className="ASB-stat__label"><Trans>Avg Leverage</Trans></span>
            <span
              className={cx("ASB-stat__value", {
                negative: marginUsagePct > 20,
                "ASB-warning": marginUsagePct > 10 && marginUsagePct <= 20,
              })}
            >
              {marginUsagePct.toFixed(1)}x
            </span>
          </div>
        </>
      )}

      {summary.posCount > 0 && active && (
        <>
          <div className="ASB-spacer" />
          <button
            className="ASB-close-all-btn"
            onClick={() => setConfirmOpen(true)}
            disabled={closing}
            title={t`Market-close all open positions`}
          >
            {closing ? t`Closing…` : t`Close All`}
          </button>
        </>
      )}

      <Modal
        isVisible={confirmOpen}
        setIsVisible={setConfirmOpen}
        label={t`Close All Positions`}
      >
        <div className="ASB-confirm-body">
          <p>
            <Trans>
              This will submit market-close requests for all{" "}
              <strong>{summary.posCount}</strong> open position
              {summary.posCount > 1 ? "s" : ""} at the current mark price.
            </Trans>
          </p>
          <p className="ASB-confirm-warn">
            <Trans>
              Execution is not guaranteed at the exact mark price. Fills may
              vary slightly depending on vault liquidity and price impact.
            </Trans>
          </p>
          <div className="ASB-confirm-actions">
            <button
              className="ASB-btn-danger"
              onClick={handleCloseAll}
              disabled={closing}
            >
              {closing ? t`Closing…` : t`Confirm Close All`}
            </button>
            <button
              className="ASB-btn-ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={closing}
            >
              <Trans>Cancel</Trans>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
